import React, { useState } from 'react';
import { modalStyles as styles } from './modalStyles';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';
import { formatDDMMYYYY } from '../utils/dates';
import RmaCustomerSummary from './RmaCustomerSummary';

const API_BASE = process.env.REACT_APP_API_URL;

const DEFAULT_TASKS = [
  'Intake inspection & condition log',
  'Data wipe / factory reset',
  'Component & hardware testing',
  'Cosmetic restoration',
  'Firmware update',
  'Final QA sign-off',
];

function StatusBadge({ status }) {
  const map = {
    pending:         { label: 'Awaiting decision',  bg: 'rgba(255,255,255,0.07)', color: colors.textMuted },
    scrapped:        { label: 'Scrapped',            bg: 'rgba(248,113,113,0.12)', color: '#f87171' },
    repairing:       { label: 'Refurb in progress', bg: 'rgba(251,146,60,0.12)',  color: '#fb923c' },
    recommissioned:  { label: 'Recommissioned',      bg: 'rgba(70,187,137,0.12)', color: colors.accent },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      padding: '0.2rem 0.65rem', borderRadius: '999px', fontSize: '0.72rem',
      fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
      backgroundColor: s.bg, color: s.color
    }}>
      {s.label}
    </span>
  );
}

export default function RmaModalClosedCase({ rma, onClose, onSave }) {
  const { getToken } = useAuth();
  const refurb = rma?.refurbishment && typeof rma.refurbishment === 'object' ? rma.refurbishment : {};
  const status = refurb.status || 'pending';

  // Decision phase state
  const [deciding, setDeciding] = useState(false);
  const [scrapNotes, setScrapNotes] = useState('');

  // Repair phase state
  const [tasks, setTasks] = useState(() => {
    if (Array.isArray(refurb.repairTasks) && refurb.repairTasks.length > 0) {
      return refurb.repairTasks;
    }
    return DEFAULT_TASKS.map(label => ({ label, done: false, note: '' }));
  });
  const [cost, setCost] = useState(refurb.repairCost != null ? String(refurb.repairCost) : '');
  const [currency, setCurrency] = useState(refurb.repairCurrency || 'EUR');
  const [repairNotes, setRepairNotes] = useState(refurb.repairNotes || '');
  const [savingTask, setSavingTask] = useState(null);
  const [savingCost, setSavingCost] = useState(false);
  const [recommissionNotes, setRecommissionNotes] = useState('');
  const [recommissioning, setRecommissioning] = useState(false);
  const [newTaskLabel, setNewTaskLabel] = useState('');

  const postDecline = rma?.postDecline && typeof rma.postDecline === 'object' ? rma.postDecline : {};
  const declinedAt = postDecline.decidedAt ? formatDDMMYYYY(postDecline.decidedAt) : null;
  const allTasksDone = tasks.length > 0 && tasks.every(t => t.done);

  const push = (updated) => {
    if (onSave) onSave(updated, { keepOpen: true });
  };

  // ── Decision ──────────────────────────────────────────────────────────────
  const handleDecide = async (decision) => {
    setDeciding(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/refurbishment/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          decision,
          notes: decision === 'scrap' ? scrapNotes : undefined
        })
      });
      const data = await res.json();
      if (data.ok) push(data.rma);
      else alert(data.error || 'Failed');
    } catch (err) {
      console.error('decide error', err);
    } finally {
      setDeciding(false);
    }
  };

  // ── Task toggle ───────────────────────────────────────────────────────────
  const handleToggleTask = async (index) => {
    const task = tasks[index];
    setSavingTask(index);
    const newDone = !task.done;
    const optimistic = tasks.map((t, i) => i === index ? { ...t, done: newDone } : t);
    setTasks(optimistic);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/refurbishment/task`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ index, done: newDone, note: task.note || '' })
      });
      const data = await res.json();
      if (data.ok) {
        const updated = data.rma?.refurbishment?.repairTasks;
        if (Array.isArray(updated)) setTasks(updated);
        push(data.rma);
      }
    } catch (err) {
      setTasks(tasks); // rollback
      console.error('task toggle error', err);
    } finally {
      setSavingTask(null);
    }
  };

  // ── Add task ──────────────────────────────────────────────────────────────
  const handleAddTask = async () => {
    const label = newTaskLabel.trim();
    if (!label) return;
    setSavingTask(-1);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/refurbishment/task`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ label, done: false })
      });
      const data = await res.json();
      if (data.ok) {
        const updated = data.rma?.refurbishment?.repairTasks;
        if (Array.isArray(updated)) setTasks(updated);
        setNewTaskLabel('');
        push(data.rma);
      }
    } catch (err) {
      console.error('add task error', err);
    } finally {
      setSavingTask(null);
    }
  };

  // ── Save cost ─────────────────────────────────────────────────────────────
  const handleSaveCost = async () => {
    setSavingCost(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/refurbishment/cost`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          cost: cost !== '' ? parseFloat(cost) : null,
          currency,
          notes: repairNotes
        })
      });
      const data = await res.json();
      if (data.ok) push(data.rma);
    } catch (err) {
      console.error('cost save error', err);
    } finally {
      setSavingCost(false);
    }
  };

  // ── Recommission ─────────────────────────────────────────────────────────
  const handleRecommission = async () => {
    setRecommissioning(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/refurbishment/recommission`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ notes: recommissionNotes })
      });
      const data = await res.json();
      if (data.ok) push(data.rma);
      else alert(data.error || 'Failed');
    } catch (err) {
      console.error('recommission error', err);
    } finally {
      setRecommissioning(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.overlay}>
      <div style={{ ...styles.modal, maxWidth: '560px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <div style={{ marginBottom: '0.3rem' }}>
              <StatusBadge status={status} />
            </div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', color: colors.textStrong }}>
              {status === 'pending' ? 'Unit Disposition' : status === 'scrapped' ? 'Unit Scrapped' : status === 'recommissioned' ? 'Recommissioned' : 'Refurbishment Repair'}
            </h2>
            <div style={{ fontSize: '0.8rem', color: colors.textMuted, marginTop: '0.15rem' }}>
              {rma.serialNumber} · {rma.rmaId || rma._id}
              {refurb.refurbId && <span style={{ color: colors.accent }}> · {refurb.refurbId}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', padding: '0.25rem' }}>✕</button>
        </div>
        <RmaCustomerSummary rma={rma} />

        {/* Context strip */}
        <div style={{
          borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)', padding: '0.6rem 0.9rem',
          display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1.25rem'
        }}>
          <div>
            <div style={{ fontSize: '0.68rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Origin</div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#fb923c' }}>Repair declined — unit retained</div>
          </div>
          {declinedAt && (
            <div>
              <div style={{ fontSize: '0.68rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Retained</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: colors.textStrong }}>{declinedAt}</div>
            </div>
          )}
          {refurb.decidedAt && (
            <div>
              <div style={{ fontSize: '0.68rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Decision</div>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: colors.textStrong }}>{formatDDMMYYYY(refurb.decidedAt)}</div>
            </div>
          )}
        </div>

        {/* ── PENDING: decision cards ── */}
        {status === 'pending' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div style={{ fontSize: '0.82rem', color: colors.textMuted, marginBottom: '0.1rem' }}>
              What should happen to this unit?
            </div>

            {/* Scrap card */}
            <div style={{
              borderRadius: '12px', border: '1px solid rgba(248,113,113,0.3)',
              backgroundColor: 'rgba(248,113,113,0.05)', padding: '1rem'
            }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#f87171', marginBottom: '0.4rem' }}>Scrap unit</div>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.84rem', color: colors.textMuted, lineHeight: 1.55 }}>
                The unit is beyond viable repair or not worth refurbishing. It will be logged as scrapped. No further action required.
              </p>
              <textarea
                value={scrapNotes}
                onChange={e => setScrapNotes(e.target.value)}
                placeholder="Reason for scrapping (optional)"
                style={{
                  width: '100%', minHeight: '56px', borderRadius: '8px', boxSizing: 'border-box',
                  border: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.04)',
                  color: colors.textBody, padding: '0.55rem 0.7rem', fontSize: '0.84rem',
                  resize: 'vertical', outline: 'none', marginBottom: '0.65rem'
                }}
              />
              <button
                onClick={() => handleDecide('scrap')}
                disabled={deciding}
                style={{
                  padding: '0.6rem 1.25rem', borderRadius: '999px', border: 'none',
                  backgroundColor: '#f87171', color: '#1a0a0a',
                  fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                  opacity: deciding ? 0.6 : 1
                }}
              >
                {deciding ? 'Saving…' : 'Confirm scrap'}
              </button>
            </div>

            {/* Refurbish card */}
            <div style={{
              borderRadius: '12px', border: `1px solid rgba(70,187,137,0.3)`,
              backgroundColor: 'rgba(70,187,137,0.05)', padding: '1rem'
            }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', color: colors.accent, marginBottom: '0.4rem' }}>Refurbish & recommission</div>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.84rem', color: colors.textMuted, lineHeight: 1.55 }}>
                The unit will be repaired and brought back to a sellable standard. A new refurb ID will be generated. Track repair tasks and costs, then recommission it back into circulation.
              </p>
              <button
                onClick={() => handleDecide('repair')}
                disabled={deciding}
                style={{
                  padding: '0.6rem 1.25rem', borderRadius: '999px', border: 'none',
                  backgroundColor: colors.accent, color: '#0e1f18',
                  fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                  opacity: deciding ? 0.6 : 1
                }}
              >
                {deciding ? 'Saving…' : 'Start refurbishment'}
              </button>
            </div>
          </div>
        )}

        {/* ── SCRAPPED ── */}
        {status === 'scrapped' && (
          <div style={{
            borderRadius: '12px', border: '1px solid rgba(248,113,113,0.25)',
            backgroundColor: 'rgba(248,113,113,0.06)', padding: '1rem'
          }}>
            <div style={{ fontWeight: 700, color: '#f87171', marginBottom: '0.5rem' }}>Unit scrapped</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
              {refurb.refurbId && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: colors.textMuted }}>Scrap reference</span>
                  <span style={{ fontFamily: 'monospace', color: colors.textStrong, fontWeight: 700 }}>{refurb.refurbId}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span style={{ color: colors.textMuted }}>Serial number</span>
                <span style={{ color: colors.textStrong, fontWeight: 600 }}>{rma.serialNumber}</span>
              </div>
              {refurb.decidedBy && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: colors.textMuted }}>Logged by</span>
                  <span style={{ color: colors.textStrong }}>{refurb.decidedBy}</span>
                </div>
              )}
              {refurb.scrappedNotes && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.84rem', color: colors.textDim, fontStyle: 'italic' }}>
                  {refurb.scrappedNotes}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── REPAIRING ── */}
        {status === 'repairing' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Refurb ID */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.84rem' }}>
              <span style={{ color: colors.textMuted }}>Refurb ID:</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: colors.accent }}>{refurb.refurbId}</span>
            </div>

            {/* Task list */}
            <div>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: colors.textMuted, marginBottom: '0.5rem' }}>
                Repair tasks — {tasks.filter(t => t.done).length}/{tasks.length}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {tasks.map((task, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.55rem 0.75rem', borderRadius: '8px',
                    border: `1px solid ${task.done ? 'rgba(70,187,137,0.25)' : 'rgba(255,255,255,0.07)'}`,
                    backgroundColor: task.done ? 'rgba(70,187,137,0.05)' : 'rgba(255,255,255,0.02)'
                  }}>
                    <button
                      disabled={savingTask === i}
                      onClick={() => handleToggleTask(i)}
                      style={{
                        width: '20px', height: '20px', borderRadius: '5px', flexShrink: 0,
                        border: `2px solid ${task.done ? colors.accent : 'rgba(255,255,255,0.2)'}`,
                        backgroundColor: task.done ? colors.accent : 'transparent',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#0e1f18', fontWeight: 800, fontSize: '0.7rem',
                        opacity: savingTask === i ? 0.5 : 1, transition: 'all 0.12s'
                      }}
                    >
                      {task.done ? '✓' : ''}
                    </button>
                    <span style={{
                      flex: 1, fontSize: '0.88rem', fontWeight: 500,
                      color: task.done ? colors.textStrong : colors.textBody,
                      textDecoration: task.done ? 'none' : 'none'
                    }}>
                      {task.label}
                    </span>
                    {task.done && task.doneBy && (
                      <span style={{ fontSize: '0.72rem', color: colors.textMuted, flexShrink: 0 }}>{task.doneBy}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Add task */}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
                <input
                  value={newTaskLabel}
                  onChange={e => setNewTaskLabel(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddTask()}
                  placeholder="Add a task…"
                  style={{
                    flex: 1, padding: '0.5rem 0.7rem', borderRadius: '8px', fontSize: '0.84rem',
                    border: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.04)',
                    color: colors.textBody, outline: 'none'
                  }}
                />
                <button
                  onClick={handleAddTask}
                  disabled={!newTaskLabel.trim() || savingTask === -1}
                  style={{
                    padding: '0.5rem 0.85rem', borderRadius: '8px', border: 'none',
                    backgroundColor: 'rgba(255,255,255,0.08)', color: colors.textStrong,
                    fontWeight: 600, fontSize: '0.84rem', cursor: 'pointer',
                    opacity: !newTaskLabel.trim() ? 0.4 : 1
                  }}
                >
                  + Add
                </button>
              </div>
            </div>

            {/* Cost */}
            <div style={{
              borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)',
              backgroundColor: 'rgba(255,255,255,0.02)', padding: '0.85rem 1rem'
            }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: colors.textMuted, marginBottom: '0.65rem' }}>
                Repair cost
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '0.4rem', flex: 1, minWidth: '140px' }}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={cost}
                    onChange={e => setCost(e.target.value)}
                    placeholder="0.00"
                    style={{
                      flex: 1, padding: '0.5rem 0.7rem', borderRadius: '8px', fontSize: '0.9rem',
                      border: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.04)',
                      color: colors.textStrong, outline: 'none', fontWeight: 600
                    }}
                  />
                  <select
                    value={currency}
                    onChange={e => setCurrency(e.target.value)}
                    style={{
                      padding: '0.5rem 0.5rem', borderRadius: '8px', fontSize: '0.84rem',
                      border: '1px solid rgba(255,255,255,0.1)', backgroundColor: 'rgba(255,255,255,0.06)',
                      color: colors.textStrong, outline: 'none', cursor: 'pointer'
                    }}
                  >
                    <option>EUR</option><option>NOK</option><option>USD</option><option>GBP</option>
                  </select>
                </div>
                <button
                  onClick={handleSaveCost}
                  disabled={savingCost}
                  style={{
                    padding: '0.5rem 0.9rem', borderRadius: '8px', border: 'none',
                    backgroundColor: 'rgba(255,255,255,0.08)', color: colors.textStrong,
                    fontWeight: 600, fontSize: '0.84rem', cursor: 'pointer',
                    opacity: savingCost ? 0.5 : 1
                  }}
                >
                  {savingCost ? 'Saving…' : 'Save cost'}
                </button>
              </div>
              <textarea
                value={repairNotes}
                onChange={e => setRepairNotes(e.target.value)}
                placeholder="Repair notes (parts used, scope, etc.)"
                style={{
                  width: '100%', minHeight: '60px', borderRadius: '8px', marginTop: '0.6rem',
                  border: '1px solid rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.03)',
                  color: colors.textBody, padding: '0.55rem 0.7rem', fontSize: '0.84rem',
                  resize: 'vertical', outline: 'none', boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Recommission */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: colors.textMuted, marginBottom: '0.6rem' }}>
                Recommission
              </div>
              <textarea
                value={recommissionNotes}
                onChange={e => setRecommissionNotes(e.target.value)}
                placeholder="Notes before recommissioning (optional)"
                style={{
                  width: '100%', minHeight: '56px', borderRadius: '8px', boxSizing: 'border-box',
                  border: '1px solid rgba(255,255,255,0.08)', backgroundColor: 'rgba(255,255,255,0.03)',
                  color: colors.textBody, padding: '0.55rem 0.7rem', fontSize: '0.84rem',
                  resize: 'vertical', outline: 'none', marginBottom: '0.65rem'
                }}
              />
              <button
                onClick={handleRecommission}
                disabled={recommissioning || !allTasksDone}
                title={!allTasksDone ? 'Complete all tasks first' : ''}
                style={{
                  width: '100%', padding: '0.75rem 1.5rem', borderRadius: '999px', border: 'none',
                  backgroundColor: allTasksDone ? colors.accent : 'rgba(255,255,255,0.06)',
                  color: allTasksDone ? '#0e1f18' : colors.textMuted,
                  fontWeight: 700, fontSize: '0.95rem',
                  cursor: allTasksDone ? 'pointer' : 'not-allowed',
                  opacity: recommissioning ? 0.6 : 1, transition: 'all 0.15s'
                }}
              >
                {recommissioning ? 'Saving…' : allTasksDone ? 'Recommission unit' : `Complete all tasks to recommission`}
              </button>
            </div>
          </div>
        )}

        {/* ── RECOMMISSIONED ── */}
        {status === 'recommissioned' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            <div style={{
              borderRadius: '12px', border: `1px solid rgba(70,187,137,0.3)`,
              backgroundColor: 'rgba(70,187,137,0.07)', padding: '1rem',
              display: 'flex', gap: '0.75rem', alignItems: 'flex-start'
            }}>
              <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>✓</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: colors.accent, marginBottom: '0.25rem' }}>Unit recommissioned</div>
                <div style={{ fontSize: '0.84rem', color: colors.textMuted }}>
                  This unit has been repaired and is back in circulation.
                </div>
                {refurb.recommissionedBy && (
                  <div style={{ fontSize: '0.8rem', color: colors.textDim, marginTop: '0.2rem' }}>
                    by {refurb.recommissionedBy}
                    {refurb.recommissionedAt ? ` · ${formatDDMMYYYY(refurb.recommissionedAt)}` : ''}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {[
                ['Refurb ID', refurb.refurbId, 'monospace'],
                ['Original serial', rma.serialNumber, null],
                ['New service tag', refurb.newSerialNumber || null, 'monospace'],
                ['Repair cost', refurb.repairCost != null ? `${refurb.repairCost} ${refurb.repairCurrency || 'EUR'}` : null, null],
              ].map(([label, value, font]) => value && (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: colors.textMuted }}>{label}</span>
                  <span style={{ color: colors.textStrong, fontWeight: 600, fontFamily: font || 'inherit' }}>{value}</span>
                </div>
              ))}
            </div>

            {refurb.recommissionedNotes && (
              <div style={{ fontSize: '0.83rem', color: colors.textDim, fontStyle: 'italic', marginTop: '0.25rem' }}>
                {refurb.recommissionedNotes}
              </div>
            )}
            {refurb.newSerialNumber && (
              <a
                href={`/device/${refurb.newDeviceId || refurb.newSerialNumber}`}
                style={{
                  display: 'inline-block', marginTop: '0.75rem',
                  padding: '0.5rem 1.1rem', borderRadius: '999px',
                  border: `1px solid rgba(70,187,137,0.35)`,
                  backgroundColor: 'rgba(70,187,137,0.1)',
                  color: colors.accent, fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none'
                }}
              >
                View refurbished device ({refurb.newSerialNumber}) →
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
